import {
  useQueryClient,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  UseMutateFunction,
} from '@tanstack/react-query';

import type { HelperResult } from '@openclassrooms/sdk-web';

import { getDependentEntities } from './dependentEntities';

type UseDeleteReturnType<ParamsType> = [
  UseMutateFunction<void, unknown, ParamsType>,
  Omit<UseMutationResult<void, unknown, ParamsType>, 'mutate'>
];

export default function getUseDelete<ParamsType>(
  remove: (params?: ParamsType) => HelperResult<void>,
  queryKey: string,
  queryKeyList: string,
  sharedCacheEntities: string[] = []
): (
  mutationOptions?: Omit<UseMutationOptions<void, unknown, ParamsType>, 'mutationFn'>
) => UseDeleteReturnType<ParamsType> {
  return (mutationOptions = {}) => {
    const queryClient = useQueryClient();

    const { mutate, ...mutationResult } = useMutation({
      ...mutationOptions,
      mutationFn: async (params: ParamsType) => {
        const response = await remove(params);
        return response?.body;
      },
      onSuccess: (data, variables, context) => {
        queryClient.removeQueries({ queryKey: [queryKey] });
        queryClient.removeQueries({ queryKey: [queryKey, variables] });
        queryClient.removeQueries({ queryKey: [queryKeyList] });

        new Set([...sharedCacheEntities, ...getDependentEntities(queryKey)]).forEach(
          (sharedCacheEntity) => {
            queryClient.removeQueries({ queryKey: [sharedCacheEntity] });
          }
        );

        mutationOptions.onSuccess?.(data, variables, context);
      },
      onSettled: (data, error, variables, context) => {
        queryClient.invalidateQueries({ queryKey: [queryKey] });
        queryClient.invalidateQueries({ queryKey: [queryKey, variables] });
        queryClient.invalidateQueries({ queryKey: [queryKeyList] });

        new Set([...sharedCacheEntities, ...getDependentEntities(queryKey)]).forEach(
          (sharedCacheEntity) => {
            queryClient.invalidateQueries({ queryKey: [sharedCacheEntity] });
          }
        );

        mutationOptions.onSettled?.(data, error, variables, context);
      },
    });

    return [mutate, mutationResult];
  };
}
